export const programData = {
  title: "Navigate Menopause",
  subtitle: "Group Coaching with Dr. Shilpa Saxena",
  description:
    "A comprehensive, expert-led program that helps you understand what's happening in your body, evaluate your options, take action, and build a plan that lasts.",
  stats: { weeks: 4, articles: 16, worksheets: 6, liveSessions: 4 },
  whatsIncluded: [
    {
      label: "4 Live Group Sessions",
      detail: "90-minute weekly sessions with Dr. Shilpa Saxena, MD — live Q&A included",
      icon: "video",
    },
    {
      label: "16+ Expert Articles & Guides",
      detail: "Evidence-based resources in your member library, organized by week",
      icon: "article",
    },
    {
      label: "Personalized Worksheets & Tools",
      detail: "Self-assessments, decision frameworks, and action plans tailored to your journey",
      icon: "worksheet",
    },
    {
      label: "Lifetime Access to Private Community",
      detail: "Private WhatsApp group for peer support and between-session connection",
      icon: "community",
    },
    {
      label: "Session Recordings — Yours to Keep",
      detail: "Every live session recorded and available within 24 hours",
      icon: "recording",
    },
    {
      label: "Follow-Up Summaries & Takeaways",
      detail: "Key insights and action items after every live session",
      icon: "summary",
    },
  ],
  weeks: [
    {
      number: 1,
      title: "Understand",
      tagline: "Replace confusion with clarity.",
      description:
        "Before you can evaluate options or take action, you need a clear picture of what's actually happening inside your body — and why your experience is completely normal.",
      liveSession: {
        id: "week-1-live",
        title: "What's Actually Happening to Your Body",
        shortTitle: "Live: Your Body",
        date: "TBD",
        time: "TBD ET",
        duration: "90 min",
        description:
          "Dr. Shilpa walks you through the biology of the menopausal transition — not as a textbook lecture, but as the explanation you wish your doctor had given you years ago.",
        topics: [
          "The difference between perimenopause, menopause, and postmenopause",
          "The hormonal cascade: estrogen, progesterone, testosterone, thyroid, and cortisol",
          "The 34+ recognized symptoms and their root causes",
          "Why your experience is uniquely yours",
          "A guided self-assessment: where you are on the menopause continuum",
        ],
        interactive:
          "Symptom mapping exercise — you'll complete your personal symptom inventory live with the group and identify your top 3 priority areas.",
        takeaway:
          "A clear picture of your current stage, a completed symptom assessment, and the language to describe what you're experiencing.",
      },
      articles: [
        {
          id: "perimenopause-vs-menopause",
          number: "1.1",
          title: "Perimenopause vs. Menopause vs. Postmenopause: A Plain-English Guide",
          shortTitle: "Stages of Menopause",
          type: "Educational article",
          readingTime: "8 min",
          description:
            'Most women are told menopause "happens around 50." The reality is far more nuanced. This guide breaks down the three stages of the menopausal transition.',
          topics: [
            "Clinical definitions vs. lived experience of each stage",
            "Timeline: what the \"average\" looks like and why averages are misleading",
            "Natural menopause, surgical menopause, and premature ovarian insufficiency",
            "When to see a provider vs. when to watch and wait",
            "How to talk to your doctor about where you are",
          ],
        },
        {
          id: "hormone-guide",
          number: "1.2",
          title: "The Hormone Guide: Estrogen, Progesterone, Testosterone & Beyond",
          shortTitle: "The Hormone Guide",
          type: "Educational article",
          readingTime: "10 min",
          description:
            "Your hormones don't just \"drop off a cliff.\" The menopausal transition involves a complex, years-long dance between multiple hormones.",
          topics: [
            "Estrogen (estradiol, estrone, estriol): the big three",
            "Progesterone: why it drops first and what that means",
            "Testosterone: the forgotten hormone in women's health",
            "Thyroid hormones: why menopause and thyroid get confused",
            "Cortisol and the stress-hormone connection",
            "The hormonal orchestra model",
          ],
        },
        {
          id: "symptom-map",
          number: "1.3",
          title: "The Symptom Map: 34 Symptoms You Might Not Know Are Connected",
          shortTitle: "34 Symptom Map",
          type: "Reference guide",
          readingTime: "6 min",
          description:
            "Hot flashes get all the attention. But the menopausal transition can affect virtually every system in your body.",
          topics: [
            "Vasomotor symptoms (hot flashes, night sweats)",
            "Neurological symptoms (brain fog, memory lapses, migraines)",
            "Psychological symptoms (anxiety, depression, mood swings)",
            "Musculoskeletal symptoms (joint pain, frozen shoulder)",
            "Metabolic symptoms (weight gain, insulin resistance, fatigue)",
            "Urogenital symptoms (vaginal dryness, UTIs, low libido)",
            "Dermatological symptoms (dry skin, hair changes)",
            "Cardiac symptoms (palpitations, blood pressure changes)",
          ],
          extras: "Includes a printable symptom checklist for your next doctor's appointment.",
        },
        {
          id: "what-your-doctor-didnt-tell-you",
          number: "1.4",
          title: "What Your Doctor Didn't Tell You (And Why)",
          shortTitle: "The Care Gap",
          type: "Perspective piece",
          readingTime: "5 min",
          description:
            "If you've felt dismissed or confused by your healthcare experience around menopause, you're not alone.",
          topics: [
            "The training gap: minimal menopause education in OB/GYN residency",
            "The post-WHI fear effect on HRT prescribing",
            "Why \"your labs are normal\" doesn't mean you feel normal",
            "Finding a provider who specializes in menopause",
            "How to evaluate whether your current provider is the right fit",
          ],
        },
      ],
      worksheets: [
        {
          id: "menopause-snapshot",
          number: "1.1",
          title: "My Menopause Snapshot",
          shortTitle: "Self-Assessment",
          type: "Self-assessment tool",
          completionTime: "15 min",
          description:
            "A structured self-assessment that captures where you are right now — your symptoms, their severity, their impact on your daily life, and what you most want to change.",
          sections: [
            "Symptom inventory (rate each of the 34 symptoms: not present / mild / moderate / severe)",
            "Quality of life impact score (sleep, work, relationships, mood, energy — rate each 1-10)",
            "What I've tried so far (medications, supplements, lifestyle changes, providers seen)",
            "My top 3 priorities (what would make the biggest difference right now?)",
            "Questions I want answered by the end of this program",
          ],
        },
      ],
      homework: "Start symptom tracking",
    },
    {
      number: 2,
      title: "Evaluate",
      tagline: "Have an informed conversation about your options.",
      description:
        "Move from understanding what's happening to knowing what you can do about it. This is the session women consistently say they wish they'd had years earlier.",
      liveSession: {
        id: "week-2-live",
        title: "Your Options, Decoded",
        shortTitle: "Live: Your Options",
        date: "TBD",
        time: "TBD ET",
        duration: "90 min",
        description:
          "Dr. Shilpa walks through the complete treatment landscape — hormonal, non-hormonal, pharmaceutical, and functional — with nuance and evidence.",
        topics: [
          "Hormone Replacement Therapy: types, delivery methods, evidence, and risk",
          "Non-hormonal prescription options: SSRIs/SNRIs, gabapentin, fezolinetant",
          "The supplement landscape: what works, what doesn't",
          "Functional medicine approaches and root-cause protocols",
          "Making decisions that fit your risk profile and values",
        ],
        interactive:
          "Decision framework exercise — map your symptoms to treatment categories and identify which approaches are worth exploring.",
        takeaway:
          "A clear understanding of the full toolkit available to you, a completed treatment decision framework, and a \"Questions for My Doctor\" card.",
      },
      articles: [
        {
          id: "hrt-explained",
          number: "2.1",
          title: "HRT Explained: The Complete Guide to Hormone Replacement Therapy",
          shortTitle: "HRT Guide",
          type: "Clinical guide",
          readingTime: "12 min",
          description:
            "HRT is the single most effective treatment for menopausal symptoms — and also the most confusing. This guide cuts through the noise.",
          topics: [
            "Systemic estrogen: oral vs. transdermal and why delivery method matters",
            "Progesterone: synthetic progestins vs. micronized — the critical distinction",
            "Testosterone: emerging evidence for libido, energy, and body composition",
            "Bioidentical vs. synthetic vs. compounded: what the terms mean",
            "The WHI study: what it found, what it didn't, and how findings have been revised",
            "Timing hypothesis: why when you start matters",
            "Contraindications and risk factors",
          ],
        },
        {
          id: "beyond-hormones",
          number: "2.2",
          title: "Beyond Hormones: Non-Hormonal Treatment Options",
          shortTitle: "Non-Hormonal Options",
          type: "Educational article",
          readingTime: "7 min",
          description:
            "Not every woman wants, needs, or can take HRT. The non-hormonal treatment landscape has expanded significantly.",
          topics: [
            "SSRIs/SNRIs for hot flashes and mood",
            "Gabapentin for hot flashes and sleep",
            "Fezolinetant (Veozah): first non-hormonal Rx for hot flashes",
            "Vaginal moisturizers and lubricants for GSM",
            "CBT for menopause: evidence for insomnia, hot flashes, and mood",
            "Acupuncture: what the evidence says",
          ],
        },
        {
          id: "supplement-guide",
          number: "2.3",
          title: "The Supplement Guide: What Works, What Doesn't, What's a Waste",
          shortTitle: "Supplement Guide",
          type: "Evidence-rated guide",
          readingTime: "8 min",
          description:
            "The menopause supplement market is enormous and largely unregulated. This guide rates the most common supplements by evidence quality.",
          topics: [
            "Black cohosh",
            "Phytoestrogens (soy isoflavones, red clover)",
            "Magnesium (glycinate, threonate, citrate)",
            "Ashwagandha and maca root",
            "Evening primrose oil",
            "Vitamin D + K2 and omega-3 fatty acids",
            "Probiotics (vaginal and gut health)",
          ],
          extras: "Includes a \"My Supplement Audit\" section to evaluate what you're currently taking.",
        },
        {
          id: "lab-tests",
          number: "2.4",
          title: "Lab Tests for Menopause: What to Ask For and How to Read Results",
          shortTitle: "Lab Tests Decoded",
          type: "Practical guide",
          readingTime: "7 min",
          description:
            "Which tests matter? What do the numbers mean? How to navigate conflicting advice from providers.",
          topics: [
            "FSH, estradiol, AMH: what they tell you and what they don't",
            "Thyroid panel: why a full panel matters",
            "Testosterone, DHEA-S, and cortisol testing",
            "Metabolic markers: fasting insulin, HbA1c, lipid panel",
            "Vitamin D, B12, ferritin, magnesium",
            "When to test and how to interpret \"normal\" ranges",
            "Sample lab order you can request from your provider",
          ],
        },
      ],
      worksheets: [
        {
          id: "treatment-decision-framework",
          number: "2.1",
          title: "My Treatment Decision Framework",
          shortTitle: "Decision Framework",
          type: "Decision-support tool",
          completionTime: "20 min",
          description:
            "Map your specific symptoms and priorities to the treatment approaches most likely to help.",
          sections: [
            "Your top 3-5 symptoms from Week 1 assessment",
            "For each symptom: treatment options ranked by evidence strength",
            "Your personal risk factors and contraindications",
            "Your values and preferences",
            "Your decision: what to explore, what to discuss with your provider, what you're not ready for",
          ],
        },
        {
          id: "questions-for-doctor",
          number: "2.2",
          title: "Questions for My Doctor",
          shortTitle: "Doctor Questions",
          type: "Appointment prep card",
          completionTime: "10 min",
          description:
            "A structured card to make your next provider visit more productive. Bring it with you.",
          sections: [
            "My current symptoms and their severity",
            "What I've tried and what happened",
            "What I'd like to discuss (specific treatments or tests)",
            "Specific questions I want answered",
            "Notes section for provider responses",
          ],
        },
      ],
      homework: "Prepare provider questions",
    },
    {
      number: 3,
      title: "Act",
      tagline: "Turn knowledge into a concrete, daily plan.",
      description:
        "The clinical understanding from Weeks 1–2 becomes the foundation for concrete, daily changes — specifically tuned to menopausal physiology.",
      liveSession: {
        id: "week-3-live",
        title: "Lifestyle as Medicine",
        shortTitle: "Live: Lifestyle",
        date: "TBD",
        time: "TBD ET",
        duration: "90 min",
        description:
          "Dr. Shilpa covers the four lifestyle pillars with the greatest impact on menopausal symptoms and long-term health.",
        topics: [
          "Nutrition: why protein matters more now, phytoestrogens, alcohol and caffeine",
          "Movement: why strength training is non-negotiable after 40",
          "Sleep: why menopausal insomnia is different, evidence-based protocols",
          "Stress regulation: the cortisol-estrogen connection, practical techniques",
        ],
        interactive:
          "\"My Week 1 Plan\" workshop — build a personalized action plan with 3 specific changes and pair up with an accountability buddy.",
        takeaway:
          "A concrete, personalized action plan, an accountability partner, and the confidence that these changes will make a difference.",
      },
      articles: [
        {
          id: "eating-for-menopause",
          number: "3.1",
          title: "Eating for Menopause: A Nutrition Guide That Actually Makes Sense",
          shortTitle: "Nutrition Guide",
          type: "Practical guide",
          readingTime: "10 min",
          description:
            "Your nutritional needs change during menopause. This guide covers what to prioritize and how to build meals that support hormonal health.",
          topics: [
            "Protein: how much you actually need, timing, and sources",
            "Phytoestrogens: soy, flax, and what the evidence says",
            "Anti-inflammatory eating: the basics without the dogma",
            "Blood sugar management: why stable glucose matters more now",
            "Alcohol and caffeine: honest answers",
            "Gut health and the estrobolome",
            "A sample 3-day meal plan",
          ],
        },
        {
          id: "strength-training",
          number: "3.2",
          title: "The Strength Training Imperative",
          shortTitle: "Strength Training",
          type: "Evidence + action guide",
          readingTime: "8 min",
          description:
            "The evidence for resistance exercise during and after menopause is overwhelming — for bone density, metabolic health, mood, and longevity.",
          topics: [
            "What happens to muscle and bone after estrogen declines",
            "The sarcopenia and osteoporosis risk window",
            "Why cardio alone isn't enough",
            "A 3-day starter routine (bodyweight to progressive)",
            "Frequency, intensity, and progression",
            "Pelvic floor considerations",
          ],
        },
        {
          id: "menopausal-insomnia",
          number: "3.3",
          title: "Fixing Menopausal Insomnia: A Sleep Protocol",
          shortTitle: "Sleep Protocol",
          type: "Step-by-step protocol",
          readingTime: "7 min",
          description:
            "Menopausal insomnia is driven by hormonal changes that alter sleep architecture and temperature regulation. Generic advice isn't enough.",
          topics: [
            "Why sleep changes (the estrogen-GABA connection)",
            "Night sweats and temperature regulation strategies",
            "The 3 a.m. wake-up: why it happens and what to do",
            "Magnesium glycinate: dosing and timing",
            "Progesterone and sleep: the clinical connection",
            "CBT-I: the gold standard for insomnia",
            "A nightly wind-down protocol",
          ],
        },
        {
          id: "stress-cortisol",
          number: "3.4",
          title: "Stress, Cortisol, and Your Nervous System",
          shortTitle: "Stress & Cortisol",
          type: "Educational + practical guide",
          readingTime: "7 min",
          description:
            "If your stress tolerance has plummeted, it's not weakness — it's biology. Estrogen modulates your nervous system's stress response.",
          topics: [
            "The estrogen-cortisol-nervous system triangle",
            "Why your reactions feel disproportionate",
            "Understanding your window of tolerance",
            "Vagus nerve stimulation techniques",
            "A 5-minute regulation protocol for acute stress",
            "When anxiety needs clinical attention",
          ],
        },
      ],
      worksheets: [
        {
          id: "week-1-action-plan",
          number: "3.1",
          title: "My Week 1 Action Plan",
          shortTitle: "Action Plan",
          type: "Action planning tool",
          completionTime: "15 min",
          description:
            "A structured, realistic plan for implementing 3 specific changes this week. Small and sustainable — not a complete overhaul.",
          sections: [
            "Nutrition: one specific change this week",
            "Movement: one commitment (specific day, time, activity)",
            "Sleep: one protocol change to try tonight",
            "My accountability buddy and check-in plan",
            "Potential obstacles and how I'll handle them",
            "End-of-week self-check (1-10)",
          ],
        },
        {
          id: "accountability-buddy",
          number: "3.2",
          title: "My Accountability Buddy Agreement",
          shortTitle: "Buddy Agreement",
          type: "Partnership tool",
          completionTime: "5 min",
          description:
            "A simple agreement with your program buddy for between-session check-ins.",
          sections: [
            "Buddy name and preferred contact method",
            "What we'll check in about",
            "When we'll check in (specific day/time)",
            "What kind of support I need",
          ],
        },
      ],
      homework: "Test your action plan",
    },
    {
      number: 4,
      title: "Sustain",
      tagline: "Build a plan that carries you forward.",
      description:
        "Integration and forward momentum. Build a long-term plan, address health priorities beyond symptom management, and create ongoing support.",
      liveSession: {
        id: "week-4-live",
        title: "Your Plan Forward",
        shortTitle: "Live: Plan Forward",
        date: "TBD",
        time: "TBD ET",
        duration: "90 min",
        description:
          "Dr. Shilpa covers the long-term health landscape and helps you build a personalized 90-day plan that integrates everything from the program.",
        topics: [
          "Bone health: osteoporosis screening, DEXA scans, prevention",
          "Cardiovascular risk: why heart disease risk increases post-menopause",
          "Brain health: estrogen-cognition connection, neuroprotective strategies",
          "Sexual health: vaginal health, libido, pelvic floor, intimacy",
          "Building your care team and annual monitoring",
          "Your 90-day plan: integrating everything into a single document",
        ],
        interactive:
          "\"My 90-Day Protocol\" — assemble your plan using materials from all 4 weeks, with group feedback and Dr. Shilpa's input.",
        takeaway:
          "Group reflection — what's changed since Week 1 — and introduction to ongoing community resources and alumni benefits.",
      },
      articles: [
        {
          id: "long-game",
          number: "4.1",
          title: "The Long Game: Bone, Heart, and Brain Health After Menopause",
          shortTitle: "Long-Term Health",
          type: "Comprehensive guide",
          readingTime: "10 min",
          description:
            "Menopause is a metabolic inflection point. The protective effects of estrogen diminish, creating new health priorities.",
          topics: [
            "Osteoporosis: the rapid bone loss window, DEXA screening, calcium + D + K2",
            "Cardiovascular health: lipid changes, blood pressure shifts",
            "Brain health: estrogen and neuroprotection, the Alzheimer's gender gap",
            "Metabolic health: insulin resistance, visceral fat, type 2 diabetes risk",
            "A recommended annual monitoring schedule",
          ],
        },
        {
          id: "sexual-health",
          number: "4.2",
          title: "Sexual Health and Intimacy: The Guide No One Gave You",
          shortTitle: "Sexual Health",
          type: "Practical guide",
          readingTime: "8 min",
          description:
            "GSM affects up to 84% of postmenopausal women, yet fewer than 7% receive treatment. Frank, clinical, and compassionate information.",
          topics: [
            "GSM explained: vaginal dryness, atrophy, recurrent UTIs",
            "Local estrogen therapy: evidence and safety profile",
            "Non-hormonal options: hyaluronic acid, DHEA (Intrarosa)",
            "Libido: hormonal, psychological, and relational factors",
            "Pelvic floor: why it matters and when to see a specialist",
            "How to talk to your partner and your doctor",
          ],
        },
        {
          id: "care-team",
          number: "4.3",
          title: "Building Your Menopause Care Team",
          shortTitle: "Your Care Team",
          type: "Action guide",
          readingTime: "5 min",
          description:
            "Navigating menopause well often requires more than one provider. Identify who you need on your team.",
          topics: [
            "The core team: primary care, gynecologist, menopause specialist",
            "When to add specialists: endocrinologist, pelvic floor PT, dietitian, therapist",
            "How to find a NAMS-certified menopause practitioner",
            "Questions to assess a provider's menopause competence",
            "Telehealth options for underserved areas",
          ],
        },
        {
          id: "annual-checklist",
          number: "4.4",
          title: "Your Annual Menopause Health Checklist",
          shortTitle: "Annual Checklist",
          type: "Reference checklist",
          readingTime: "4 min",
          description:
            "A printable checklist of screenings, lab tests, and health conversations organized by frequency.",
          topics: [
            "Every visit: blood pressure, weight, symptom review, medication review",
            "Annually: lipid panel, fasting glucose/HbA1c, thyroid, vitamin D",
            "Every 1-2 years: mammogram, pelvic exam, skin check",
            "Baseline: DEXA scan, colonoscopy, cardiovascular risk assessment",
            "As needed: hormone levels, metabolic testing, mental health screening",
          ],
          extras: "Space to record your results and track year-over-year changes.",
        },
      ],
      worksheets: [
        {
          id: "90-day-protocol",
          number: "4.1",
          title: "My 90-Day Menopause Protocol",
          shortTitle: "90-Day Protocol",
          type: "Comprehensive personal plan",
          completionTime: "25 min",
          description:
            "The capstone deliverable — a single document pulling together everything from all 4 weeks into an actionable 90-day plan.",
          sections: [
            "My current stage and top symptoms",
            "My treatment strategy — what I'm pursuing and discussing",
            "My lifestyle plan — nutrition, movement, sleep, stress management",
            "My monitoring schedule — tests and screenings",
            "My care team — current and needed providers",
            "30-day check-in",
            "60-day check-in",
            "90-day review — am I where I want to be?",
          ],
        },
      ],
      homework: "Finalize your 90-day protocol",
    },
  ],
  community: {
    whatsapp: {
      title: "Private WhatsApp Community",
      description:
        "Connect with your cohort between sessions. Share wins, ask questions, and find support from women who understand exactly what you're going through.",
    },
    recordings: {
      title: "Session Recordings Library",
      description:
        "All live sessions are recorded and available within 24 hours. Yours to keep and revisit whenever you need a refresher.",
    },
  },
  about: {
    doctor: {
      name: "Dr. Shilpa Saxena, MD",
      title: "Board-Certified Family Medicine Physician",
      description:
        "Dr. Shilpa Saxena is a board-certified family medicine physician who has made it her mission to help women navigate the menopausal transition with clarity, confidence, and evidence-based care.",
    },
    organization: {
      name: "Forum Health",
      description:
        "Forum Health is a nationwide network of integrative and functional medicine providers dedicated to finding the root cause of your health issues.",
    },
  },
};

export function getWeekItems(week) {
  const items = [];
  items.push({
    id: week.liveSession.id,
    title: week.liveSession.shortTitle || week.liveSession.title,
    type: "live",
  });
  week.articles.forEach((a) => {
    items.push({ id: a.id, title: a.shortTitle || a.title, type: "article", number: a.number });
  });
  week.worksheets.forEach((w) => {
    items.push({
      id: w.id,
      title: w.shortTitle || w.title,
      type: "worksheet",
      number: w.number,
    });
  });
  return items;
}

export function findContentById(id) {
  for (const week of programData.weeks) {
    if (week.liveSession.id === id) {
      return { type: "live", content: week.liveSession, week };
    }
    for (const article of week.articles) {
      if (article.id === id) {
        return { type: "article", content: article, week };
      }
    }
    for (const worksheet of week.worksheets) {
      if (worksheet.id === id) {
        return { type: "worksheet", content: worksheet, week };
      }
    }
  }
  return null;
}

export function getAllItems() {
  const items = [];
  for (const week of programData.weeks) {
    items.push({ id: week.liveSession.id, type: "live", week });
    for (const article of week.articles) {
      items.push({ id: article.id, type: "article", week });
    }
    for (const worksheet of week.worksheets) {
      items.push({ id: worksheet.id, type: "worksheet", week });
    }
  }
  return items;
}
